import {Component, OnInit} from '@angular/core';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {PageComponent} from '../../page.component';
import {ErrorModel} from '../../../../model/error.model';
import {EmployeeService} from '../../../../service/employee.service';
import {SessionStorageService} from '../../../../service/session-storage.service';
import {HttpStatusCode} from '@angular/common/http';

/**
 * @class
 * @component
 * @description This class maintains the sign-out page component of our application.
 * @export
 * @name SignOutEmployeePageComponent
 */
@Component({
  imports: [
    MatCard,
    MatCardAvatar,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatIcon,
    PageComponent
  ],
  selector: 'app-page-employee-sign-out',
  standalone: true,
  styleUrl: './sign-out.component.scss',
  templateUrl: './sign-out.component.html'
})
export class SignOutEmployeePageComponent implements OnInit {

  /**
   * @description This property contains any errors that occurred during the execution of the page component.
   * @memberof SignOutEmployeePageComponent
   * @name SignOutEmployeePageComponent#error
   * @property
   * @public
   * @type {ErrorModel?}
   */
  public error?: ErrorModel;

  /**
   * @constructor
   * @description This method instantiates a new instance of our sign-out page component with injected dependencies.
   * @param {EmployeeService} employeeService The injected instance of our PrismHR Employee service provider.
   * @param {SessionStorageService} sessionStorageService The injected instance of our session storage service provider.
   * @memberof SignOutEmployeePageComponent
   * @name SignOutEmployeePageComponent#constructor
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our PrismHR Employee service provider.
     * @memberof SignOutEmployeePageComponent
     * @name SignOutEmployeePageComponent#employeeService
     * @private
     * @readonly
     * @type {EmployeeService}
     */
    private readonly employeeService: EmployeeService,
    /**
     * @description This property contains the injected instance of our session storage service provider.
     * @memberof SignOutEmployeePageComponent
     * @name SignOutEmployeePageComponent#sessionStorageService
     * @private
     * @readonly
     * @type {SessionStorageService}
     */
    private readonly sessionStorageService: SessionStorageService
  ) {
  }

  /**
   * @description This method initializes the component.
   * @memberof SignOutEmployeePageComponent
   * @method
   * @name SignOutEmployeePageComponent#ngOnInit
   * @public
   * @returns {void}
   */
  public ngOnInit(): void {

    // Ensure we have an online session, simply return if not.
    if (!this.sessionStorageService.online(EmployeeService.SessionStorageServiceKey)) return;

    // Delete the existing session from the .NET backend API.
    this.employeeService.signOut().subscribe({

      // Catch any errors that occurred during the sign-out process.
      error: (error: Error): void => {

        // Reset the error model so the view is updated.
        this.error = ErrorModel.fromError(error, HttpStatusCode.FailedDependency,
          'Failed to sign out of the PrismHR Employee session.', 'logout')
      },

      // Handle the response from the .NET backend API.
      next: (): void => {

        // Clear the session from the session storage service provider.
        this.sessionStorageService.delete(EmployeeService.SessionStorageServiceKey);
      }
    });
  }
}
